import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { PageComponent } from 'src/app/calvin-emr-shared/page/page.component';
import { MedicationDialogComponent } from 'src/app/medications/med-dialog/med-dialog.component';
import { Medication } from 'src/app/calvin-emr-shared/models/medication.model';
import { MedicationService } from 'src/app/calvin-emr-shared/services/medication.service';
import { Subscription } from 'rxjs';
import {
  BARCODE_ERROR_DOWNLOAD,
  BARCODE_PDF_NAME,
  BARCODE_SUFFIX,
  ERROR,
  SUCCESS,
} from 'src/app/global';
import { Table, TableModule } from 'primeng/table';
import { ToastService } from 'src/app/calvin-emr-shared/services/toast.service';

// To download barcodes
import * as JsBarcode from 'jsbarcode';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import { DialogService } from 'primeng/dynamicdialog';
import { ButtonDirective } from 'primeng/button';

import { CheckboxModule } from 'primeng/checkbox';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { FileUploadDialogComponent } from '../../shared/file-upload-dialog/file-upload-dialog.component';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { ToastModule } from 'primeng/toast';

/**
 * The page that fetches all medications from the backend and displays them in a table.
 * The Medication List page also handles the creation of dialogs that are used to create,
 * update, delete, and import medications.
 */
@Component({
  selector: 'app-medications-page',
  templateUrl: './medications-page.component.html',
  styleUrls: ['./medications-page.component.scss'],
  providers: [DialogService],
  standalone: true,
  imports: [
    ButtonDirective,
    CheckboxModule,
    FormsModule,
    TableModule,
    InputTextModule,
    TooltipModule,
    ConfirmationDialogComponent,
    FileUploadDialogComponent,
    IconField,
    InputIcon,
    ToastModule
],
})
export class MedicationsPageComponent extends PageComponent implements OnInit, OnDestroy {
  /**
   * List containing all Medication objects.
   */
  medications: Medication[] = [];

  /**
   * List containing only the IDs for all medications.
   */
  ids: string[] = [];

  /**
   * Subscription to the MedicationsService.
   */
  medicationsSub?: Subscription;

  /**
   * Columns to show up in the Medication table.
   */
  private importErrors: string[] = [];

  /**
   * The PrimeNG table HTML element.
   */
  @ViewChild('medTable') medTable: Table | undefined;

  /**
   * The fields to filter the table by when using the search bar.
   */
  tableFilterFields: string[] = ['name', 'id', 'routes'];
  selectedMedications: Medication[] = [];

  // Dialog flags
  showImportMedicationsDialog = false;
  showDeleteMedicationsDialog = false;
  confirmDeleteMedications = false;

  private medService = inject(MedicationService);
  public dialogService = inject(DialogService);
  private toastService = inject(ToastService);

  /**
   * Calls function to load medications.
   */
  ngOnInit(): void {
    super.ngOnInit();
    this.getMedications();
  }

  /**
   * Unsubscribe from all active subscriptions.
   */
  ngOnDestroy(): void {
    this.medicationsSub?.unsubscribe();
  }

  /**
   * Gets all objects from the "medications" collection in Firestore and converts
   * them into Medication objects. Sorts the medications by name and assigns the
   * 'ids' property an array of medication ids (for use in the dialog form validation).
   */
  getMedications(): void {
    this.startLoading();
    this.medicationsSub = this.medService.getAllMedications().subscribe((actionArray) => {
      this.medications = actionArray.map((item: any) => {
        const med = new Medication();
        const obj = {
          id: item.payload.doc.id,
          ...(item.payload.doc.data() as object),
        };
        Object.assign(med, obj);
        return med;
      });
      // Sort medications by name
      this.medications.sort((a, b) => a.name.localeCompare(b.name));

      this.ids = this.medications.map((med) => med.id);

      this.stopLoading();
    });
  }

  /**
   * Opens a MedicationDialogComponent for the user to create/edit a medication.
   */
  openMedicationDialog(med?: any) {
    const medication = med ? med : new Medication();
    const action = med ? 'Edit' : 'Create';

    const dialogRef = this.dialogService.open(MedicationDialogComponent, {
      header: `${action} Medication`,
      data: {
        action: action,
        medication: medication,
        ids: this.ids,
      },
      width: '50%',
    });

    // med will not be defined if user clicks "Cancel" in dialog
    dialogRef?.onClose.subscribe((med?: Medication) => {
      if (med) {
        this.toastService.clear();
        this.medService.upsertMedication(med).subscribe(
          () => {
            this.toastService.addOne({
              severity: SUCCESS,
              summary: 'Success',
              detail: `${action === 'Create' ? 'Created' : 'Updated'} medication ${med.id}: ${
                med.name
              }!`,
            });
          },
          (error) => {
            this.toastService.addOne({
              severity: ERROR,
              summary: 'Error',
              detail: `Unable to ${action.toLocaleLowerCase()} medication ${med.name}!`,
            });
          }
        );
      }
    });
  }

  /**
   * Creates a hidden HTMLCanvasElement that contains the barcode image.
   *
   * @param med
   * @returns HTMLCanvasElement of the generated barcode
   *
   */
  generateHTMLCanvasElement(med: Medication): HTMLCanvasElement {
    JsBarcode('#barcode', med.getId(), {
      width: 2.5,
      height: 100,
      text: med.getName(),
      // text: med.getId() + ' ' + med.getName(),
    });

    // Reference: https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/toBlob
    return document.getElementById('barcode') as HTMLCanvasElement;
  }

  /**
   * Saves a PNG of the specified medication's barcode to the user's Downloads folder.
   *
   * @param med Medication to download barcode for
   */
  public downloadBarcode(med: Medication) {
    const barcode = this.generateHTMLCanvasElement(med);
    barcode.toBlob((blob) => {
      if (blob) {
        saveAs(blob, med.getId() + BARCODE_SUFFIX);
      } else {
        console.log(BARCODE_ERROR_DOWNLOAD);
      }
    });
  }

  /**
   * Downloads barcodes for all selected medications into a PDF.
   * Reference: https://github.com/parallax/jsPDF.
   */
  public downloadBarcodes() {
    const pdf = new jsPDF(); // create PDF object
    const pageHeight = pdf.internal.pageSize.height; // get default page height (in mm)
    const x = 10; // barcodes will all be 10mm to the right of the page
    let y = 10; // starting point of page will be 10mm from the top edge
    this.selectedMedications.forEach((med) => {
      const barcode = this.generateHTMLCanvasElement(med); // generate barcode for each medication
      const w = barcode.width / 4; // since barcodes tend to be large, scale by
      const h = barcode.height / 4; //  1/4 of original size
      const w70 = w * 0.7;
      const h70 = h * 0.7;
      const w50 = w * 0.5;
      const h50 = h * 0.5;

      // Check if the barcode will go off the page; if so, add a new page
      if (y + h + h70 + h50 + 30 >= pageHeight) {
        pdf.addPage();
        y = 10; // Restart height position
      }

      pdf.addImage(barcode, 'png', x, y, w, h);
      y += h + 10; // will make the next barcode 10mm below the current one

      pdf.addImage(barcode, 'png', x, y, w70, h70);
      y += h70 + 5;

      pdf.addImage(barcode, 'png', x, y, w50, h50);
      y += h50 + 10;
    });

    // Save the PDF to the user's downloads folder.
    pdf.save(BARCODE_PDF_NAME);
  }

  /**
   * Opens a FileUploadDialogComponent for the user to upload a CSV.
   */
  openImportMedDialog() {
    this.showImportMedicationsDialog = true;
  }

  onCloseImportMedicationsDialog(file?: Blob) {
    if (file) {
      const fileReader = new FileReader();
      fileReader.onload = (progressEvent) => {
        const fileResult = progressEvent.target?.result as string;
        const medArray = this.getMedsFromFile(fileResult);
        if (medArray.length > 0) {
          this.createMeds(medArray);
        }
      };
      fileReader.readAsText(file);
    }
  }

  openDeleteMedicationsDialog() {
    this.showDeleteMedicationsDialog = true;
  }

  onCloseDeleteMedicationsDialog(didConfirmDelete: boolean) {
    if (didConfirmDelete) {
      this.toastService.clear();
      this.selectedMedications.forEach((med: Medication) => {
        this.medService.deleteMedication(med.id).subscribe(
          () => {
            this.toastService.addOne({
              severity: SUCCESS,
              summary: 'Success',
              detail: `Deleted medication ${med.id}: ${med.name}!`,
            });
          },
          (error) => {
            this.toastService.addOne({
              severity: ERROR,
              summary: 'Error',
              detail: `Unable to delete medication ${med.id}: ${med.name}`,
            });
          }
        );
      });
    } else {
      this.confirmDeleteMedications = false;
    }
  }

  getMedsFromFile(result: string) {
    const medArray: Medication[] = [];

    // Clear any previous import errors
    this.importErrors = [];

    // Ignore first row, since first row is column headers
    const newMeds = result.split('\r').slice(1);

    newMeds.forEach((medString) => {
      // Try to add data; if data is not valid, add to errors array
      try {
        const params = medString
          .split(',')
          .filter((x) => x != '')
          .map((x) => x.trim());
        const routes = params[1].split('/').sort((a, b) => a.localeCompare(b));

        // Parameter order: id, name, route
        const med = new Medication(params[2], params[0], routes);
        if (!this.isValidMedication(med)) {
          throw new Error();
        }
        medArray.push(med);
      } catch (e) {
        this.importErrors.push(medString);
      }
    });

    // Display errors, if any
    if (this.importErrors.length > 0) {
      const errorString = this.importErrors.join('\n');
      console.error(
        'Could not import the following data:',
        errorString,
        '\nVerify that your file is correctly formatted and that no data is missing.'
      );
      // TODO: make this a dialog box
    }

    return medArray;
  }

  /**
   * Function that is called when the user imports medications via CSV.
   * Cleans and creates new medications based on the data.
   * Errors are put into the "importErrors" array.
   *
   * @param medArray list of medications imported from a CSV
   */
  createMeds(medArray: Medication[]) {
    this.startLoading();
    this.toastService.clear();
    medArray.forEach((medication: Medication) => {
      this.medService.upsertMedication(medication).subscribe(
        () => {
          this.stopLoading();
        },
        (error) => {
          this.toastService.addOne({
            severity: ERROR,
            summary: 'Error',
            detail: `Unable to import medication ${medication.id}: ${medication.name}!`,
            life: 5000,
          });
        }
      );
    });
  }

  /**
   * Filters the data in the table based on the text in the search field
   * HTML input element. The data is filtered based on whether the any
   * of the fields in the tableFilterFields array contain the text.
   *
   * @param target The search field HTML input element.
   */
  filterTable(target: EventTarget | null) {
    if (target) {
      this.medTable?.filterGlobal((target as HTMLInputElement).value, 'contains');
    }
  }

  isValidMedication(med: Medication): boolean {
    return true;
  }

  get didSelectMeds(): boolean {
    return this.selectedMedications.length > 0;
  }
}
